import {
  and,
  ControlProps,
  RankedTester,
  rankWith,
  uiTypeIs,
} from "@jsonforms/core";
import { useJsonForms, withJsonFormsControlProps } from "@jsonforms/react";
import { Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  CALENDAR_EVENT_TYPE,
  IScheduledVisits,
  RenderersUIType,
} from "@home-health-notify/domain";

import dayjs from "dayjs";
import {
  ErrorText,
  VisitItem,
  VisitsContainer,
  VisitsLabel,
  VisitsWrapper,
} from "./VisitPickerRenderer.style";
import { ValidationModeJFC } from "@home-health-notify/templates";

const VisitPickerRenderer = ({
  path,
  data,
  handleChange,
  uischema,
  required,
  config,
}: ControlProps) => {
  const { label } = uischema;
  const chat = config?.chat;
  const { core } = useJsonForms();
  const formData = core?.data ?? {};
  const errors = core?.errors ?? [];
  const validationMode = core?.validationMode;
  const isShowValidation = validationMode === ValidationModeJFC.ValidateAndShow;

  const [scheduledVisits, setScheduledVisits] = useState<IScheduledVisits[]>(
    [],
  );
  const [visitPickerError, setVisitPickerError] = useState<string | null>(null);

  const getVisitPickerError = (field: string) => {
    const error = errors.find(
      (error: any) => error.params?.missingProperty === field,
    );
    return error && "is a required property";
  };

  useEffect(() => {
    if (errors && errors.length > 0) {
      const errorMsg = getVisitPickerError("visitToCancel");
      setVisitPickerError(errorMsg ?? null);
    } else {
      setVisitPickerError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  const fetchScheduledVisits = async () => {
    if (chat) {
      const selectedRole = config?.selectedRole;
      await chat.getScheduleVisits({
        type: CALENDAR_EVENT_TYPE.PROJECTED,
        ...(selectedRole && { departmentShorthand: selectedRole }),
        startDate: formData.visitDate,
        endDate: dayjs(formData.visitDate || formData.nextVisitDate)
          .endOf("day")
          .valueOf(),
      });
      setScheduledVisits(chat.scheduledVisits);
    }
  };

  useEffect(() => {
    if (formData.visitDate) {
      fetchScheduledVisits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.visitDate]);

  const onChange = (
    id: string,
    visitStartTime: number,
    visitEndTime: number,
  ) => {
    handleChange(path, {
      id,
      visitStartTime,
      visitEndTime,
    });
  };

  return (
    <VisitsWrapper>
      <VisitsLabel>
        {label as string} {required && "*"}
      </VisitsLabel>
      <VisitsContainer>
        {scheduledVisits.length === 0 ? (
          <Typography>No scheduled visits found for the day.</Typography>
        ) : (
          scheduledVisits.map(({ id, schedulerTime, schedulerEndTime }) => {
            const visitStartTime = dayjs(schedulerTime).format("hh:mm A");
            const visitEndTime = dayjs(schedulerEndTime).format("hh:mm A");
            const visitTime = `${visitStartTime} - ${visitEndTime}`;
            const isSelected = data?.id === id;
            return (
              <VisitItem
                isSelected={isSelected}
                key={id}
                onClick={() => {
                  if (
                    typeof schedulerTime === "number" &&
                    typeof schedulerEndTime === "number"
                  ) {
                    onChange(id, schedulerTime, schedulerEndTime);
                  }
                }}
                size="small"
                label={visitTime}
                className={isSelected ? ".selected" : undefined}
              />
            );
          })
        )}
      </VisitsContainer>
      {isShowValidation && visitPickerError && (
        <ErrorText>{visitPickerError}</ErrorText>
      )}
    </VisitsWrapper>
  );
};

export const visitPickerTester: RankedTester = rankWith(
  5,
  and(uiTypeIs(RenderersUIType.VisitPicker)),
);

export default withJsonFormsControlProps(VisitPickerRenderer);
